import cls from "@fvilers/cls";
import { ComponentPropsWithoutRef, useEffect, useRef } from "react";
import { useProgrammaticFocus } from "../../hooks/useProgrammaticFocus";
import { DialogContextProvider } from "./contexts/DialogContextProvider";
import styles from "./Dialog.module.css";
import { useDialogHandler } from "./hooks/useDialogHandler";

export { DialogActions } from "./DialogActions";
export { DialogBody } from "./DialogBody";
export { DialogClose } from "./DialogClose";
export { DialogCloseButton } from "./DialogCloseButton";
export { DialogHeader } from "./DialogHeader";

export type DialogProps = {
  dismissible?: boolean;
  onCancel?: () => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
} & ComponentPropsWithoutRef<"dialog">;

export function Dialog({
  children,
  className,
  dismissible = true,
  onCancel,
  onOpenChange,
  open = false,
  ...props
}: DialogProps) {
  const focus = useProgrammaticFocus();
  const ref = useRef<HTMLDialogElement>(null);
  useDialogHandler({ dismissible, onCancel, onOpenChange, open, ref });

  useEffect(() => {
    const dialogEl = ref.current;
    if (!dialogEl || !open) return;

    requestAnimationFrame(() => {
      const focusTarget =
        dialogEl.querySelector<HTMLElement>("[data-autofocus]");
      focus(focusTarget);
    });
  }, [open, focus]);

  return (
    <dialog className={cls(styles.Dialog, className)} ref={ref} {...props}>
      <DialogContextProvider
        onOpenChange={(open) => {
          onOpenChange?.(open);
          if (!open) {
            ref.current?.close();
          }
        }}
      >
        {children}
      </DialogContextProvider>
    </dialog>
  );
}
